import { useState, useEffect } from 'react';
import { 
    Sparkles, 
    Trophy, 
    Medal,
    DollarSign,
    Clock,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    Target,
    Truck,
    Route,
    AlertTriangle,
    CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getRankIcon = (rank) => {
    switch (rank) {
        case 1: return <Trophy className="w-5 h-5 text-yellow-400" />;
        case 2: return <Medal className="w-5 h-5 text-zinc-400" />;
        case 3: return <Medal className="w-5 h-5 text-amber-600" />;
        default: return <span className="text-zinc-500">#{rank}</span>;
    }
};

const getScoreBadgeColor = (score) => {
    if (score >= 0.7) return 'bg-green-500/20 text-green-400 border-green-600';
    if (score >= 0.5) return 'bg-yellow-500/20 text-yellow-400 border-yellow-600';
    return 'bg-orange-500/20 text-orange-400 border-orange-600';
};

export default function ChainOptimizerPanel({ truckId, onSelectChain }) {
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedChain, setExpandedChain] = useState(null);

    useEffect(() => {
        if (truckId) {
            fetchLastResult();
        }
    }, [truckId]);

    const fetchLastResult = async () => {
        try {
            const response = await fetch(`${API_URL}/api/optimizer/truck/${truckId}`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                if (data.ranked_chains?.length > 0) {
                    setResult(data);
                }
            }
        } catch (error) {
            console.error('Error fetching optimizer result:', error);
        }
    };

    const runOptimizer = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/optimizer/truck/${truckId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });
            
            if (response.ok) {
                const data = await response.json();
                setResult(data);
                
                if (data.ranked_chains?.length > 0) {
                    toast.success(`Found ${data.legal_chains_found} legal chains from ${data.total_chains_evaluated} evaluated`);
                } else {
                    toast.warning('No legal chain combinations found');
                }
            } else {
                toast.error('Failed to run optimizer');
            }
        } catch (error) {
            console.error('Error running optimizer:', error);
            toast.error('Error running optimizer');
        } finally {
            setLoading(false);
        }
    };

    if (!truckId) {
        return null;
    }

    return (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="chain-optimizer-panel">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-zinc-300 uppercase tracking-wider flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-400" />
                        Chain Optimizer
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            runOptimizer();
                        }}
                        disabled={loading}
                        className="border-purple-600 text-purple-400 hover:bg-purple-600/20 text-xs"
                        data-testid="optimize-chains-btn"
                    >
                        {loading ? (
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                            <Sparkles className="w-3 h-3 mr-1" />
                        )}
                        Optimize
                    </Button>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {!result ? (
                    <div className="text-center py-4 text-zinc-500 text-sm">
                        Click "Optimize" to find the best load chains
                    </div>
                ) : result.ranked_chains?.length === 0 ? (
                    <div className="text-center py-4">
                        <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                        <p className="text-zinc-400 text-sm">No legal chains found</p>
                        <p className="text-zinc-600 text-xs mt-1">
                            {result.total_chains_evaluated} chains evaluated
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Summary */}
                        <div className="flex items-center justify-between text-xs text-zinc-500 border-b border-zinc-800 pb-2">
                            <span>{result.legal_chains_found} legal / {result.total_chains_evaluated} total</span>
                            <span>{result.optimization_time_ms?.toFixed(0)}ms</span>
                        </div>

                        {/* Ranked Chains */}
                        <div className="space-y-2">
                            {result.ranked_chains.map((chain, idx) => (
                                <Collapsible 
                                    key={idx}
                                    open={expandedChain === idx}
                                    onOpenChange={() => setExpandedChain(expandedChain === idx ? null : idx)}
                                >
                                    <div className={`border rounded-lg overflow-hidden ${
                                        chain.rank === 1 ? 'border-yellow-600/50 bg-yellow-500/5' :
                                        chain.rank === 2 ? 'border-zinc-600/50' :
                                        'border-zinc-800'
                                    }`}>
                                        <CollapsibleTrigger className="w-full">
                                            <div className="p-3 flex items-center justify-between hover:bg-zinc-800/30 cursor-pointer">
                                                <div className="flex items-center gap-3">
                                                    {getRankIcon(chain.rank)}
                                                    <div className="text-left">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-white font-medium">
                                                                ${chain.metrics.revenue_total.toLocaleString()}
                                                            </span>
                                                            <Badge className={`text-xs ${getScoreBadgeColor(chain.score)}`}>
                                                                {(chain.score * 100).toFixed(0)}%
                                                            </Badge>
                                                        </div>
                                                        <div className="text-zinc-500 text-xs">
                                                            {chain.load_ids.length} loads · ${chain.metrics.revenue_per_legal_hour}/hr
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {chain.metrics.resets_required > 0 && (
                                                        <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                                                            {chain.metrics.resets_required} Reset
                                                        </Badge>
                                                    )}
                                                    {expandedChain === idx ? (
                                                        <ChevronUp className="w-4 h-4 text-zinc-500" />
                                                    ) : (
                                                        <ChevronDown className="w-4 h-4 text-zinc-500" />
                                                    )}
                                                </div>
                                            </div>
                                        </CollapsibleTrigger>
                                        
                                        <CollapsibleContent>
                                            <div className="border-t border-zinc-800 p-3 space-y-3 bg-zinc-900/50">
                                                {/* Loads in chain */}
                                                <div className="space-y-1">
                                                    <p className="text-zinc-500 text-xs uppercase">Route</p>
                                                    {chain.loads.map((load, i) => (
                                                        <div key={i} className="flex items-center gap-2 text-xs">
                                                            <Route className="w-3 h-3 text-zinc-600" />
                                                            <span className="text-zinc-400">{load.origin}</span>
                                                            <span className="text-zinc-600">→</span>
                                                            <span className="text-zinc-400">{load.destination}</span>
                                                            <span className="text-green-400 ml-auto">${load.gross_pay}</span>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Metrics */}
                                                <div className="grid grid-cols-3 gap-2 text-xs">
                                                    <div>
                                                        <p className="text-zinc-600">Total Time</p>
                                                        <p className="text-white">{chain.metrics.total_chain_time_hours?.toFixed(1)}h</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-zinc-600">Deadhead</p>
                                                        <p className="text-white">{chain.metrics.deadhead_miles?.toFixed(0)} mi</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-zinc-600">Loaded</p>
                                                        <p className="text-white">{chain.metrics.loaded_miles?.toFixed(0)} mi</p>
                                                    </div>
                                                </div>

                                                {/* Home alignment */}
                                                {chain.projected_return_home && (
                                                    <div className="flex items-center gap-2 text-xs">
                                                        <Target className="w-3 h-3 text-blue-400" />
                                                        <span className="text-zinc-400">Return:</span>
                                                        <span className="text-white">
                                                            {new Date(chain.projected_return_home).toLocaleDateString()}
                                                        </span>
                                                        <span className="text-zinc-500">
                                                            ({chain.adjusted_days_out?.toFixed(1)} days)
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Why ranked */}
                                                <div className="border-t border-zinc-800 pt-2">
                                                    <p className="text-zinc-600 text-xs mb-1">Why this chain:</p>
                                                    <div className="flex flex-wrap gap-1">
                                                        {chain.explanation_summary?.why_ranked?.map((reason, i) => (
                                                            <Badge key={i} className="bg-zinc-800 text-zinc-400 text-xs">
                                                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                                                {reason}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Action button */}
                                                {onSelectChain && (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onSelectChain(chain);
                                                        }}
                                                        data-testid={`view-chain-${idx}-btn`}
                                                    >
                                                        View Details
                                                    </Button>
                                                )}
                                            </div>
                                        </CollapsibleContent>
                                    </div>
                                </Collapsible>
                            ))}
                        </div>

                        {/* Timestamp */}
                        {result.timestamp && (
                            <div className="text-center text-zinc-600 text-xs pt-2">
                                Last optimized: {new Date(result.timestamp).toLocaleTimeString()}
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
